import React from 'react';
import { Helmet } from 'react-helmet-async';

// Site-wide constants
const SITE_NAME = 'Branding Yard';
const DEFAULT_TITLE = 'Branding Yard | Premium Corporate Branding Products';
const DEFAULT_DESCRIPTION = 'Elevate your corporate identity with premium branding products. Business cards, executive pens, leather diaries, and custom corporate apparel. Quality craftsmanship for distinguished businesses.';
const DEFAULT_IMAGE = '/images/og-default.jpg'; // You'll need to add this image
const SITE_URL = typeof window !== 'undefined' ? window.location.origin : 'https://brandingyard.co.zw';

/**
 * SEO Component for managing page-specific meta tags
 * 
 * @param {string} title - Page title (will be appended with site name)
 * @param {string} description - Meta description for the page
 * @param {string} image - Open Graph image URL
 * @param {string} url - Canonical URL for the page
 * @param {string} type - Open Graph type (website, article, product)
 * @param {object} product - Product data for product schema
 * @param {array} breadcrumbs - Breadcrumb items for BreadcrumbList schema
 * @param {boolean} noIndex - Set to true to prevent indexing
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image,
  url,
  type = 'website',
  product = null,
  breadcrumbs = null,
  noIndex = false,
  children
}) => {
  const pageTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const pageDescription = description || DEFAULT_DESCRIPTION;
  const pageImage = image || `${SITE_URL}${DEFAULT_IMAGE}`;
  const pageUrl = url || (typeof window !== 'undefined' ? window.location.href : SITE_URL);

  // Organization Schema (for homepage)
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/images/logos/logo.png`,
    description: DEFAULT_DESCRIPTION,
    sameAs: [
      'https://www.facebook.com/share/1C8zWXjoSK/'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: 'English'
    }
  };

  // Product Schema (for product pages)
  const productSchema = product ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image_url,
    sku: product.id,
    brand: {
      '@type': 'Brand',
      name: SITE_NAME
    },
    offers: {
      '@type': 'Offer',
      url: pageUrl,
      priceCurrency: product.currency || 'USD',
      price: product.price,
      availability: product.stock > 0 
        ? 'https://schema.org/InStock' 
        : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: SITE_NAME
      }
    },
    ...(product.averageRating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: product.averageRating,
        reviewCount: product.reviewCount || 0
      }
    })
  } : null;

  // BreadcrumbList Schema
  const breadcrumbSchema = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? `${SITE_URL}${item.url}` : undefined
    }))
  } : null;

  // WebSite Schema (for search box)
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/store?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={pageUrl} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Open Graph Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Product-specific OG tags */}
      {product && (
        <>
          <meta property="product:price:amount" content={product.price} />
          <meta property="product:price:currency" content={product.currency || 'USD'} />
          <meta property="product:availability" content={product.stock > 0 ? 'in stock' : 'out of stock'} />
        </>
      )}

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>

      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>

      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}

      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {/* Additional meta tags passed as children */}
      {children}
    </Helmet>
  );
};

export default SEO;

// Export constants for use in other components
export { SITE_NAME, DEFAULT_DESCRIPTION, SITE_URL };
